import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(language === 'pt' ? 'As passwords não coincidem' : 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError(language === 'pt' ? 'Password deve ter pelo menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || (language === 'pt' ? 'Erro ao redefinir password' : 'Error resetting password');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-red-100 p-4 rounded-full">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-4">
            {language === 'pt' ? 'Link Inválido' : 'Invalid Link'}
          </h2>
          <p className="text-gray-600 mb-6">
            {language === 'pt' 
              ? 'Este link é inválido ou expirou. Por favor, peça um novo link de recuperação.'
              : 'This link is invalid or has expired. Please request a new recovery link.'}
          </p>
          <Link
            to="/forgot-password"
            className="inline-block px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600"
          >
            {language === 'pt' ? 'Pedir Novo Link' : 'Request New Link'}
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-green-100 p-4 rounded-full">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-4">
            {language === 'pt' ? 'Password Redefinida!' : 'Password Reset!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {language === 'pt' 
              ? 'A sua password foi alterada com sucesso. Já pode fazer login com a nova password.'
              : 'Your password has been changed successfully. You can now login with your new password.'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600"
          >
            {language === 'pt' ? 'Ir para Login' : 'Go to Login'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8">
        {/* Logo */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          <img
            src="/icon-192x192.png"
            alt="Marketplace TVDE"
            className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-200"
          />
          <span className="text-3xl font-bold text-slate-900">
            Marketplace <span className="text-emerald-500">TVDE</span>
          </span>
        </div>

        <h2 className="text-2xl font-bold text-center mb-2">
          {language === 'pt' ? 'Nova Password' : 'New Password'}
        </h2>
        <p className="text-center text-gray-600 mb-6">
          {language === 'pt' 
            ? 'Insira a sua nova password.'
            : 'Enter your new password.'}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'pt' ? 'Nova Password' : 'New Password'}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={language === 'pt' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'pt' ? 'Confirmar Password' : 'Confirm Password'}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={language === 'pt' ? 'Repita a password' : 'Repeat password'}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading 
              ? (language === 'pt' ? 'A processar...' : 'Processing...') 
              : (language === 'pt' ? 'Redefinir Password' : 'Reset Password')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
